/**
 * RCIntegrationPanel.jsx
 * ──────────────────────
 * RingCentral Integration panel for Settings → Integrations. Renders the
 * "Connected Services" summary card (M365 + RC) plus the detailed RC
 * connection / disconnect / activity-log surface.
 *
 * Extracted from `Settings.jsx` (Feb 2026) to keep the parent page under
 * 1000 lines.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Mail, Phone, Activity, AlertTriangle } from "lucide-react";
import DeliveryStatusBadge from "@/components/DeliveryStatusBadge";
import MyRingCentralConnect from "@/components/rc/MyRingCentralConnect";
import MyOutlookConnect from "@/components/MyOutlookConnect";

export default function RCIntegrationPanel({ token, role }) {
  const [rcStatus, setRcStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [tenantIdInput, setTenantIdInput] = useState("");
  const isAdmin = ["super_admin", "crm_admin"].includes(role);
  const h = { Authorization: `Bearer ${token}` };

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/integrations/rc/status`, { headers: h });
      setRcStatus(r.data);
      setTenantIdInput(r.data.rc_tenant_id || "");
    } catch { setRcStatus({ rc_connection_status: "disconnected", gateway_configured: false }); }
    setLoading(false);
  };
  const fetchLog = async () => {
    try { const r = await axios.get(`${API}/integrations/rc/activity`, { headers: h }); setActivityLog(r.data || []); } catch { /* swallow */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchStatus defined above; run once on mount
  useEffect(() => { fetchStatus(); }, []);

  // Auto-refresh the activity log every 8s while it's expanded so webhook updates appear in near-real-time
  useEffect(() => {
    if (!showLog) return;
    const id = setInterval(fetchLog, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLog]);

  const handleConnect = async () => {
    if (!tenantIdInput.trim()) { toast.error("Enter a Tenant ID"); return; }
    setConnecting(true);
    try {
      await axios.post(`${API}/integrations/rc/connect`, { rc_tenant_id: tenantIdInput.trim() }, { headers: h });
      toast.success("RingCentral connected");
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Connection failed"); }
    setConnecting(false);
  };
  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/integrations/rc/disconnect`, {}, { headers: h });
      toast.success("RingCentral disconnected");
      fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
  };

  if (loading) return <div className="card" style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading integration status...</div>;

  const st = rcStatus?.rc_connection_status || "disconnected";
  const statusColors = { connected: { bg: "#DAFBE8", color: "#1D9E75", label: "Connected" }, disconnected: { bg: "#f5f5f5", color: "#888", label: "Disconnected" }, error: { bg: "#fff1f0", color: "#ff3b30", label: "Error" } };
  const sc = statusColors[st] || statusColors.disconnected;

  return (
    <div data-testid="settings-integrations">
      {/* Per-rep OAuth — every rep connects their own RingCentral account so
         the team can dial concurrently from individual caller-IDs. Rendered
         first because most reps will only need this card. */}
      <MyRingCentralConnect token={token} role={role} />

      {/* Per-rep M365 OAuth — each rep connects their own Outlook so emails
         to/from their leads sync into the lead timeline. Tenant never has
         a tenant-wide power: the CRM only sees this rep's mailbox. */}
      <MyOutlookConnect token={token} role={role} />

      <div className="card" style={{ padding: "20px 24px", marginBottom: 14, marginTop: 14 }}>
        <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Connected Services</h2>
        {/* RingCentral — dynamic. (Microsoft 365 / Outlook is per-rep and
           rendered above in MyOutlookConnect — no separate tenant-level row.) */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: "#FF6A0015", display: "flex", alignItems: "center", justifyContent: "center" }}><Phone style={{ width: 18, height: 18, color: "#FF6A00" }} /></div>
          <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 520 }}>RingCentral</div><div style={{ fontSize: 10, color: sc.color }}>{sc.label}</div></div>
          <span style={{ padding: "3px 10px", borderRadius: 10, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.color }}>{sc.label}</span>
        </div>
      </div>

      {/* RC Detailed Settings */}
      <div className="card" style={{ padding: "20px 24px" }} data-testid="rc-settings-panel">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <Phone style={{ width: 16, height: 16, color: "#FF6A00" }} />
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>RingCentral Integration</h3>
          {!rcStatus?.gateway_configured && <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 9, background: "#FFF3CD", color: "#856404" }}>Gateway not configured</span>}
        </div>

        {/* Status Tiles */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          <div style={{ background: "#f9f9f9", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, textTransform: "uppercase", color: "#888" }}>Status</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: sc.color, marginTop: 2 }}>{sc.label}</div>
          </div>
          <div style={{ background: "#f9f9f9", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, textTransform: "uppercase", color: "#888" }}>Tenant ID</div>
            <div style={{ fontSize: 11, fontWeight: 500, marginTop: 2, fontFamily: "monospace" }}>{rcStatus?.rc_tenant_id || "—"}</div>
          </div>
          <div style={{ background: "#f9f9f9", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, textTransform: "uppercase", color: "#888" }}>Last sync</div>
            <div style={{ fontSize: 11, marginTop: 2 }}>{rcStatus?.rc_last_sync_at ? new Date(rcStatus.rc_last_sync_at).toLocaleString() : "Never"}</div>
          </div>
          <div style={{ background: "#f9f9f9", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, textTransform: "uppercase", color: "#888" }}>Gateway</div>
            <div style={{ fontSize: 11, fontWeight: 500, marginTop: 2, color: rcStatus?.gateway_configured ? "#1D9E75" : "#888" }}>{rcStatus?.gateway_configured ? "Configured" : "Not set"}</div>
          </div>
        </div>

        {/* Error banner */}
        {rcStatus?.rc_last_error && (
          <div style={{ background: "#fff1f0", border: "0.5px solid #ffccc7", borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }} data-testid="rc-error-banner">
            <AlertTriangle style={{ width: 14, height: 14, color: "#ff3b30", flexShrink: 0 }} />
            <div style={{ fontSize: 12, color: "#A32D2D" }}>{rcStatus.rc_last_error}</div>
          </div>
        )}

        {/* Admin: Connect/Disconnect */}
        {isAdmin && (
          <div style={{ borderTop: "0.5px solid #eee", paddingTop: 14 }}>
            {st !== "connected" ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Connect to RingCentral Gateway</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <input className="input" value={tenantIdInput} onChange={e => setTenantIdInput(e.target.value)} placeholder="RC Tenant ID (from gateway admin)" style={{ flex: 1 }} data-testid="rc-tenant-id-input" />
                  <button onClick={handleConnect} disabled={connecting} className="btn btn-primary" style={{ fontSize: 12, whiteSpace: "nowrap" }} data-testid="rc-connect-btn">
                    {connecting ? "Connecting..." : "Connect"}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>Enter the Tenant ID provided by your AriaOne administrator. This connects your CRM to the RingCentral telephony gateway.</div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1D9E75" }}>RingCentral is active</div>
                  <div style={{ fontSize: 11, color: "#888" }}>CRM users can make calls and send SMS via the Sales Dialer and Communications modules.</div>
                </div>
                <button onClick={handleDisconnect} style={{ border: "0.5px solid #ffccc7", color: "#A32D2D", background: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }} data-testid="rc-disconnect-btn">Disconnect</button>
              </div>
            )}
          </div>
        )}

        {/* Non-admin: status only */}
        {!isAdmin && st !== "connected" && (
          <div style={{ background: "#FFF3CD", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#856404" }}>
            RingCentral is not connected. Contact your admin to set up the integration in Settings.
          </div>
        )}

        {/* Activity Log Toggle */}
        <div style={{ borderTop: "0.5px solid #eee", paddingTop: 14, marginTop: 14 }}>
          <button onClick={() => { setShowLog(!showLog); if (!showLog) fetchLog(); }} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: "#2563eb" }} data-testid="rc-activity-toggle">
            <Activity style={{ width: 12, height: 12 }} /> {showLog ? "Hide" : "Show"} activity log
          </button>
          {showLog && (
            <div style={{ marginTop: 10, maxHeight: 260, overflow: "auto" }} data-testid="rc-activity-log">
              {activityLog.length === 0 && <div style={{ fontSize: 12, color: "#888", padding: 8 }}>No RC activity yet</div>}
              {activityLog.map((a, i) => (
                <div key={a.id || i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0", borderBottom: "0.5px solid #eee", fontSize: 11 }} data-testid={`rc-activity-row-${i}`}>
                  <span style={{ color: "#888", flexShrink: 0, width: 140 }}>
                    {(a.updated_at || a.created_at) ? new Date(a.updated_at || a.created_at).toLocaleString() : ""}
                  </span>
                  <span style={{ color: "#334155", fontWeight: 500, minWidth: 80 }}>
                    {a.type?.replace("rc_", "").replace("_", " ")}
                  </span>
                  <span style={{ color: "#64748b", minWidth: 120 }}>{a.phone || ""}</span>
                  <DeliveryStatusBadge status={a.delivery_status || a.result} errorCode={a.error_code} />
                  <span style={{ color: "#aaa", marginLeft: "auto" }}>{a.user_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Iter 165 (Josh fire-drill) — Webhook subscription health.
            Surfaces the persisted rc_tenant_settings doc with a derived
            health pill so admins can spot the same outage Tatiana hit
            (subscription expired May 21, callback pointed at a dead
            preview hostname) without a Mongo shell. Includes a one-
            click Resubscribe button that calls the new auto-renewal
            sweep. Admin-only — passes through the same RBAC the
            backend enforces. */}
        {isAdmin && <RCWebhookHealthCard token={token} />}
      </div>
    </div>
  );
}

function RCWebhookHealthCard({ token }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [renewing, setRenewing] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const refresh = async () => {
    setState((p) => ({ ...p, loading: true, error: null }));
    try {
      const r = await axios.get(`${API}/integrations/ringcentral/webhook-health`, { headers });
      setState({ loading: false, data: r.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null,
        error: e?.response?.data?.detail || e.message });
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const resubscribe = async () => {
    if (renewing) return;
    setRenewing(true);
    try {
      const { data } = await axios.post(`${API}/integrations/ringcentral/webhook-renew`, {}, { headers });
      if (data.action === "renewed") {
        toast.success(`Subscription renewed — expires ${data.expires_at?.slice(0, 16) || "in 7 days"}`);
      } else if (data.action === "no_renewal_needed") {
        toast.success("Subscription is healthy — no renewal needed.");
      } else if (data.action === "skipped" && data.reason === "no_RC_WEBHOOK_PUBLIC_URL") {
        toast.error("RC_WEBHOOK_PUBLIC_URL env var not set on this deployment. Add it (e.g. https://crm.ariaone.ai) then click Resubscribe again.");
      } else {
        toast.error(`Renewal ${data.action}: ${data.reason || data.error || "see logs"}`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renewal failed");
    } finally {
      setRenewing(false);
    }
  };

  const healthPill = (h) => {
    const map = {
      ok:               { label: "Healthy",         bg: "#dcfce7", color: "#166534", border: "#86efac" },
      expiring_soon:    { label: "Expiring soon",   bg: "#fef3c7", color: "#854d0e", border: "#fde68a" },
      expired:          { label: "Expired",         bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
      callback_drift:   { label: "Callback drift",  bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
      no_subscription:  { label: "Not subscribed",  bg: "#fee2e2", color: "#991b1b", border: "#fecaca" },
      unknown:          { label: "Unknown",         bg: "#e2e8f0", color: "#475569", border: "#cbd5e1" },
    };
    return map[h] || map.unknown;
  };

  return (
    <div data-testid="rc-webhook-health-card" style={{
      marginTop: 16, padding: 14,
      background: "var(--card-bg, #fff)",
      border: "0.5px solid var(--border, #e2e8f0)",
      borderRadius: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            RingCentral Webhook Subscription
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
            Required for live call-recording + transcript ingestion. RC max 7-day lifetime.
          </div>
        </div>
        {state.data && (() => {
          const p = healthPill(state.data.health);
          return (
            <span data-testid={`rc-webhook-health-pill-${state.data.health}`} style={{
              padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.05em",
              background: p.bg, color: p.color, border: `0.5px solid ${p.border}`,
            }}>{p.label}</span>
          );
        })()}
      </div>

      {state.loading && <div style={{ fontSize: 11, color: "#94a3b8" }}>Loading…</div>}
      {state.error && <div style={{ fontSize: 11, color: "#991b1b" }}>Couldn't load: {state.error}</div>}

      {state.data && (
        <div data-testid="rc-webhook-health-body" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px", fontSize: 11, color: "#334155", marginBottom: 10 }}>
          <span style={{ color: "#94a3b8" }}>Subscription ID</span>
          <span style={{ fontFamily: "monospace" }}>{state.data.subscription_id || "—"}</span>

          <span style={{ color: "#94a3b8" }}>Callback URL</span>
          <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{state.data.callback_url || "—"}</span>

          {state.data.desired_callback_url && state.data.desired_callback_url !== state.data.callback_url && (
            <>
              <span style={{ color: "#94a3b8" }}>Expected (RC_WEBHOOK_PUBLIC_URL)</span>
              <span style={{ fontFamily: "monospace", color: "#991b1b", wordBreak: "break-all" }}>{state.data.desired_callback_url}</span>
            </>
          )}

          <span style={{ color: "#94a3b8" }}>Expires at</span>
          <span>{state.data.expires_at || "—"}{
            Number.isFinite(state.data.seconds_to_expiry) && state.data.seconds_to_expiry < 0
              ? ` (expired ${Math.floor(-state.data.seconds_to_expiry / 86400)}d ago)`
              : Number.isFinite(state.data.seconds_to_expiry) && state.data.seconds_to_expiry < 86400 * 2
                ? ` (in ${Math.floor(state.data.seconds_to_expiry / 3600)}h)`
                : ""
          }</span>

          <span style={{ color: "#94a3b8" }}>Last auto-renewed</span>
          <span>{state.data.last_renewed_at || "never"} {state.data.last_renewal_reason ? `· ${state.data.last_renewal_reason}` : ""}</span>

          <span style={{ color: "#94a3b8" }}>Auto-renewal loop</span>
          <span style={{ color: state.data.autorenew_dormant ? "#991b1b" : "#166534", fontWeight: 600 }}>
            {state.data.autorenew_dormant
              ? "Dormant — RC_WEBHOOK_PUBLIC_URL not set"
              : "Active (re-checks every 30 min)"}
          </span>
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          data-testid="rc-webhook-resubscribe-btn"
          disabled={renewing}
          onClick={resubscribe}
          style={{
            padding: "6px 12px", fontSize: 11, fontWeight: 600,
            background: "#0ea5e9", color: "#fff", border: 0, borderRadius: 6,
            cursor: renewing ? "wait" : "pointer", fontFamily: "inherit",
            opacity: renewing ? 0.65 : 1,
          }}
        >
          {renewing ? "Resubscribing…" : "Resubscribe now"}
        </button>
        <button
          type="button"
          onClick={refresh}
          style={{
            padding: "6px 12px", fontSize: 11, fontWeight: 600,
            background: "transparent", color: "#475569",
            border: "0.5px solid #cbd5e1", borderRadius: 6,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
